import React, { useEffect, useLayoutEffect, useState, useRef, useMemo, useCallback } from 'react';
import LockerList from '../../locker/lockerList/LockerList';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import resourceTimeGridPlugin from '@fullcalendar/resource-timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import resourcePlugin from '@fullcalendar/resource';
import rrulePlugin from '@fullcalendar/rrule';
import moment from 'moment';
import CustModal from '../../common/custmodal/CustModal';
import AppointmentPopup from './AppointmentPopup';

import { AnyIfEmpty, useDispatch, useSelector } from 'react-redux';
import { getVisits } from '../../../redux/visits/actions/visits.action';
import { eventColors, resources10Lockers, resources12Lockers } from '../../../constants/constants';
import { AppDispatch, IRootState } from '../../../redux/rootReducer';
import { generateVendorEvents } from '../../../utils/eventGenerator';

import DeleteAppointmentModal from '../../common/DeleteAppointmentModal/DeleteAppointmentModal';
import CameraIcon from '../../svgIcons/CameraIcon';
import VendorAppointmentPopup from './VendorAppointmentPopup';
import DeleteVendorAppointmentModal from '../../common/DeleteVendorAppointmentModal/DeleteVendorAppointmentModal';
import MaintenanceIcon from '../../svgIcons/MaintennaceIcon';
import styles from './AppointmentFullCalendar.module.css';
import { DatesSetArg, EventContentArg, MoreLinkContentArg } from '@fullcalendar/core';
import AppointmentTable from './AppointmentTable/AppointmentTable';
import { visits, Vendor } from './types';
import Loader from '../../common/loader/Loader';


interface props {
    locationId: string;
    lockersCount: number;
    handleDatesSet?: (args: DatesSetArg) => void;
    handleDateRangeChange?: (startDate: string, endDate: string) => void;
    viewMode?: 'calendar' | 'list';
}

const Calendar = ({ locationId, lockersCount, handleDatesSet, handleDateRangeChange, viewMode = 'list' }: props) => {
    const [calendarInfo, setCalendarInfo] = useState<visits | Vendor | null>(null);
    const [open, setOpen] = useState<boolean>(true);
    const [deleteModalOpen, setDeleteModalOpen] = useState<boolean>(false);
    const [showAppointmentModal, setShowAppointmentModal] = useState<boolean>(false);
    const [showVendorAppointmentModal, setShowVendorAppointmentModal] = useState<boolean>(false);
    const [timeStamp, setTimeStamp] = useState(false);
    const dispatch: AppDispatch = useDispatch();
    const appointmentLoading = useSelector((state: IRootState) => state.appointmentReducer.loading);
    const { vendor, appointment } = useSelector((state: IRootState) => state.appointmentReducer.data);
    const calendarRef = useRef<FullCalendar>(null);
    const [startDate, setStartDate] = useState<string | null>(null);
    const [endDate, setEndDate] = useState<string | null>(null);

    // Track narrow/mobile widths so the FullCalendar toolbar and sizing can adapt.
    // The library's default aspectRatio-based height assumes a single-row toolbar;
    // on phone widths the toolbar wraps (see index.css) so we let content size the
    // calendar naturally instead of clipping it to a fixed aspect ratio.
    const [isMobileView, setIsMobileView] = useState(
        () => typeof window !== 'undefined' && window.innerWidth <= 640
    );

    useEffect(() => {
        const handleResize = () => setIsMobileView(window.innerWidth <= 640);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const handleDatesSetCallback = useCallback(
        (args: DatesSetArg) => {
            const start = moment(args.start).format('YYYY-MM-DD');

            const end = moment(args.end)
                .subtract(1, 'day')
                .format('YYYY-MM-DD');

            setStartDate(start);
            setEndDate(end);

            // agar parent ko bhi batana ho
            handleDatesSet?.(args);
        },
        [handleDatesSet]
    );

    const isVendorAppointment = (info: visits | Vendor | null): info is Vendor => {
        return !!info && 'vendorEmail' in info;
    };

    // Memoize resources to prevent recreation on every render
    const resources = useMemo(() => {
        return lockersCount === 10 ? resources10Lockers : resources12Lockers;
    }, [lockersCount]);

    // Prevent duplicate API calls
    const lastFetchKeyRef = useRef<string | null>(null);
    const prevLocationIdRef = useRef<string | null>(null);

    // After property change: reset date range so fetch matches the active view. List defaults to this
    // week; calendar resets to current month and syncs FullCalendar. useLayoutEffect runs before fetch effect.
    useLayoutEffect(() => {
        if (!locationId) return;
        if (prevLocationIdRef.current === null) {
            prevLocationIdRef.current = locationId;
            return;
        }
        if (prevLocationIdRef.current === locationId) return;
        prevLocationIdRef.current = locationId;

        lastFetchKeyRef.current = null;

        if (viewMode === 'list') {
            const start = moment().startOf('week').format('YYYY-MM-DD');
            const end = moment().endOf('week').format('YYYY-MM-DD');
            setStartDate(start);
            setEndDate(end);
        } else {
            const start = moment().startOf('month').format('YYYY-MM-DD');
            const end = moment().endOf('month').format('YYYY-MM-DD');
            setStartDate(start);
            setEndDate(end);

            const api = calendarRef.current?.getApi();
            if (api) {
                api.gotoDate(moment().startOf('month').toDate());
            }
        }
    }, [locationId, viewMode]);

    useEffect(() => {
        if (!locationId || !startDate || !endDate) return;

        const fetchKey = `${locationId}_${startDate}_${endDate}`;

        if (lastFetchKeyRef.current === fetchKey) return;

        dispatch(getVisits(locationId, startDate, endDate));
        lastFetchKeyRef.current = fetchKey;
    }, [locationId, startDate, endDate, dispatch]);

    // Handle calendar resize when sidebar expands/collapses
    useEffect(() => {
        const resizeObserver = new ResizeObserver(() => {
            const calendarApi = calendarRef.current?.getApi();
            if (calendarApi) {
                // Small delay to ensure DOM has updated
                setTimeout(() => {
                    calendarApi.updateSize();
                }, 100);
            }
        });

        const calendarContainer = document.querySelector('.outletConainer');
        if (calendarContainer) {
            resizeObserver.observe(calendarContainer);
        }

        return () => {
            resizeObserver.disconnect();
        };
    }, []);

    const handleEventClick = useCallback((e: AnyIfEmpty<object>) => {
        const props = e?.event?.extendedProps;
        setCalendarInfo(props);

        if (props?.vendorName) {
            // This is a vendor appointment
            setShowVendorAppointmentModal(true);
            setShowAppointmentModal(false);
        } else {
            // This is a regular appointment
            setShowAppointmentModal(true);
            setShowVendorAppointmentModal(false);
        }

        setOpen(true);
    }, []);

    const renderEventContent = useCallback((eventInfo: EventContentArg) => {
        const extendedProps = eventInfo?.event?._def?.extendedProps || {};

        const { vendorName, hasAdminDeleted, deletedAt, unitYardiId, unitName, locker_number, isInvited, photoCount } = extendedProps;

        const isDeleted = hasAdminDeleted === 1 || deletedAt !== null;

        // If vendor exists, return the vendor block (only this will be returned)
        if (vendorName) {
            return (
                <div className="event-info-container vendor-event">
                    <MaintenanceIcon color="#2d8a5f" size={14} style={{ minWidth: '14px' }} />
                    <p className="event-info-text">{vendorName}</p>
                </div>
            );
        }

        // Otherwise return the appointment block
        return (
            <div className={`event-info-container appointment-event ${isDeleted ? 'deleted' : ''}`}>
                <p className="event-info-text">
                    {unitYardiId ? unitYardiId : unitName}
                    <span className={styles.appointmentLockerNumber}>
                        {locker_number}
                        {isInvited === 'true' && <span>{`: S`}</span>}
                    </span>
                </p>

                {photoCount > 0 && (
                    <div className="camera-container">
                        <CameraIcon
                            className="camera-icon"
                            fill={isDeleted ? '#f5004a' : undefined}
                            width={16}
                            height={16}
                            aria-hidden={false}
                        // title="Photos attached"
                        />
                    </div>
                )}
            </div>
        );
    }, []);

    const onShowDeleteModal = useCallback(() => {
        setDeleteModalOpen(true);
        setShowAppointmentModal(false);
    }, []);

    // Memoize datesSet handler
    // const handleDatesSetCallback = useCallback((args: DatesSetArg) => {
    //     handleDatesSet?.(args);
    // }, [handleDatesSet]);

    // Memoize moreLinkContent handler
    const moreLinkContentCallback = useCallback((args: MoreLinkContentArg) => {
        return {
            html: `<div class='custom-more-visits'>${args?.shortText} <span>more</span></div>`,
        };
    }, []);

    const handleDeleteClick = (visit: visits | Vendor) => {
        setCalendarInfo(visit);
        setDeleteModalOpen(true);
    };

    // Memoize events array to prevent unnecessary re-renders
    const calendarEvents = useMemo(() => {
        return [
            ...(appointment ?? []).map((visit: visits) => ({
                ...visit,
                resourceId: resources.find(
                    (resource) => resource.locker_number === visit.locker_number,
                )?.id,
                title: `${visit?.unitYardiId ? visit?.unitYardiId : visit.unitName}-${visit.locker_number}`,
                start: moment(`${visit.appointmentDate} ${visit.appointmentStartTime}`, 'YYYY-MM-DD hh:mm A').format(),
                end: moment(`${visit.appointmentDate} ${visit.appointmentEndTime}`, 'YYYY-MM-DD hh:mm A').format(),
                textColor: visit?.hasAdminDeleted === 1 || visit?.deletedAt !== null ? '#f5004a' : '#000000',
                classNames: [
                    'calendarTitle',
                    visit?.hasAdminDeleted === 1 || visit?.deletedAt !== null
                        ? 'deleted'
                        : eventColors.find((event) => event.locker_number === visit.locker_number)?.backgroundCss || '',
                ],
                backgroundColor:
                    visit?.hasAdminDeleted === 1 || visit?.deletedAt !== null
                        ? '#fff1f1'
                        : eventColors.find((event) => event.locker_number === visit.locker_number)?.backgroundColor,
                borderColor: visit?.hasAdminDeleted === 1 || visit?.deletedAt !== null ? '#f5004a' : 'transparent',
            })),
            ...(vendor ?? [])
                .map((vendorinfo: Vendor) => {
                    const generatedEvents = generateVendorEvents(vendorinfo);

                    return generatedEvents.map((event) => ({
                        ...event,
                        resourceId: resources.find(
                            (r) => r.locker_number === vendorinfo.lockerNumber,
                        )?.id,
                    }));
                })
                .flat(),
        ];
    }, [appointment, vendor, resources]);

    // Handle row click for table view
    const handleRowClick = useCallback((item: visits | Vendor, isVendor: boolean) => {
        setCalendarInfo(item);
        if (isVendor) {
            setShowVendorAppointmentModal(true);
            setShowAppointmentModal(false);
        } else {
            setShowAppointmentModal(true);
            setShowVendorAppointmentModal(false);
        }
        setOpen(true);
    }, []);

    return (
        <div className={styles.calendarContainer}>
            {appointmentLoading && <Loader />}
            {showAppointmentModal && (
                <CustModal
                    open={open}
                    setOpen={setOpen}
                    className="appointment-popup availibility-popup"
                    bodyData={
                        <AppointmentPopup
                            setOpen={setOpen}
                            locationId={locationId}
                            calendarInfo={calendarInfo}
                            setShowTimeStamp={setTimeStamp}
                            setShowDeleteModal={onShowDeleteModal}
                        />
                    }
                />
            )}

            {showVendorAppointmentModal && (
                <CustModal
                    open={open}
                    setOpen={setOpen}
                    className="vendorAppointment-popup"
                    bodyData={
                        <VendorAppointmentPopup
                            setOpen={setOpen}
                            locationId={locationId}
                            calendarInfo={calendarInfo}
                            setShowDeleteModal={onShowDeleteModal}
                        />
                    }
                />
            )}
            {
                <CustModal
                    open={timeStamp}
                    setOpen={setTimeStamp}
                    bodyData={<LockerList calendarInfo={calendarInfo} setOpen={setTimeStamp} />}
                />
            }

            {viewMode === 'calendar' ? (
                <FullCalendar
                    ref={calendarRef}
                    datesSet={handleDatesSetCallback}
                    weekends={true}
                    plugins={[rrulePlugin, dayGridPlugin, timeGridPlugin, resourceTimeGridPlugin, interactionPlugin, resourcePlugin]}
                    eventClick={handleEventClick}
                    selectable={true}
                    slotEventOverlap={true}
                    navLinks={true}
                    allDaySlot={false}
                    // monthMode={false}
                    initialView="dayGridMonth"
                    headerToolbar={{
                        left: 'prev,next today',
                        center: 'title',
                        right: 'dayGridMonth,timeGridWeek,resourceTimeGridDay',
                    }}
                    // On phone widths the toolbar wraps onto multiple rows (see index.css),
                    // so a fixed aspectRatio clips the calendar body. height="auto" lets the
                    // whole calendar (toolbar + grid) size itself to its actual content instead.
                    height={isMobileView ? 'auto' : undefined}
                    aspectRatio={isMobileView ? undefined : 1.8}
                    dayMaxEvents={3}
                    moreLinkClick={() => 'resourceTimeGridDay'}
                    moreLinkContent={moreLinkContentCallback}
                    resources={resources}
                    displayEventTime={false}
                    eventContent={renderEventContent}
                    events={calendarEvents}
                    // On phone widths the week/day (resource) grids were squeezing every
                    // column down to a sliver, truncating day names ("Sur Mo Tue...") and
                    // locker labels ("Lc Lc Lc..."). Give those views a sane minimum column
                    // width on mobile so labels stay readable; FullCalendar then makes the
                    // grid horizontally scrollable instead of crushing the columns. Month
                    // view is untouched since it already renders correctly on phones.
                />
            ) : (
                <AppointmentTable
                    key={locationId}
                    appointment={appointment}
                    vendor={vendor}
                    handleRowClick={handleRowClick}
                    handleDeleteClick={handleDeleteClick}
                    apiStartDate={startDate}
                    apiEndDate={endDate}
                    onApiDateRangeChange={(s, e) => {
                        setStartDate(s);
                        setEndDate(e);
                        handleDateRangeChange?.(s, e);
                    }}
                />
            )}
            {deleteModalOpen && (
                <CustModal
                    open={deleteModalOpen}
                    setOpen={setDeleteModalOpen}
                    className="delete-appointment-modal"
                    bodyData={
                        isVendorAppointment(calendarInfo) ? (
                            <DeleteVendorAppointmentModal
                                calendarInfo={calendarInfo}
                                setShowDeleteModal={setDeleteModalOpen}
                                setShowVendorAppointmentModal={setShowVendorAppointmentModal}
                                locationId={locationId}
                                startDate={startDate}
                                endDate={endDate}
                            />
                        ) : (
                            <DeleteAppointmentModal
                                calendarInfo={calendarInfo}
                                setShowDeleteModal={setDeleteModalOpen}
                                locationId={locationId}
                                startDate={startDate}
                                endDate={endDate}
                            />
                        )
                    }
                />
            )}
        </div>
    );
};

export default Calendar;
